import { Box, Flex, Typography } from "@jewlr/storybook/core"
import { Input } from "@jewlr/storybook/form"
import PropTypes from "prop-types"
import React, { useState, useEffect } from "react"
import { Helmet } from "react-helmet"
import { Switch } from "react-router-dom"
import styled from "styled-components"

import GenericNotFound from "app/generic-not-found"
import Category from "areas/helpdesk/category"
import Collection from "areas/helpdesk/collection"
import Contact from "areas/helpdesk/contact"
import Homepage from "areas/helpdesk/homepage"
import { Breadcrumbs } from "areas/helpdesk/index"
import SearchResults from "areas/helpdesk/search-results"
import WrappedArticle from "areas/helpdesk/wrapped-article"
import { Route } from "helpers/sentry"
import useDebounce from "helpers/use-debounce"
import withHelpdesk from "hoc/helpdesk/with-helpdesk"

const Header = styled(Box)`
  text-align: center;
  word-break: break-all;
`
const SearchBar = styled(Box)`
  ${({ theme }) => `
    background-color: ${theme.helpdesk.input.background};
    border: ${theme.helpdesk.input.border};
    box-sizing: border-box;
    font-size:${theme.helpdesk.input.fontSize || "19px"};
    font-weight:500;
    height: ${theme.space[6]};
    letter-spacing: 0.86px;
    line-height: 23px;
    margin-bottom: ${theme.space[4]};
    margin: auto;
    padding: ${theme.helpdesk.input.container_padding};
  `}
`
const Search = styled(Input)`
  ${({ theme }) => `
    background: ${theme.helpdesk.input.background_search_icon || ""};
    background-color: ${theme.helpdesk.input.background};
    border: 0px !important;
    padding: ${theme.helpdesk.input.padding || "14px 0 8px"};
  `}
`

const Helpdesk = ({
  fetchResults,
  helpdeskArticle,
  helpdeskCollection,
  helpdeskRelatedArticles,
  location,
  collections,
  staticContext,
}) => {
  const [searchValue, setSearchValue] = useState("")
  const debouncedSearchTerm = useDebounce(searchValue, 1000)

  useEffect(() => {
    fetchResults(debouncedSearchTerm)
  }, [debouncedSearchTerm])

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search)
    const queryParam = urlParams.get("query")

    if (queryParam && queryParam != searchValue) {
      setSearchValue(queryParam)
      fetchResults(queryParam)
    }
  }, [location.search])

  useEffect(() => {
    if (!location.pathname.includes("search")) {
      setSearchValue("")
    }
  }, [location.pathname])

  const serverNotFound =
    helpdeskArticle?.not_found || helpdeskCollection?.not_found

  if (serverNotFound) {
    return (
      <div>
        <Helmet>
          <title>404 Page Not Found</title>
        </Helmet>
        <GenericNotFound staticContext={staticContext} />
      </div>
    )
  }

  const showSearchText = ["/helpdesk", "/helpdesk/search"].includes(
    location.pathname
  )

  return (
    <Box contain px="0">
      <Helmet>
        <title>FAQ</title>
      </Helmet>
      <Header px={{ _: 3, tablet: 0 }}>
        <Box pb={2} pt={4}>
          {showSearchText && (
            <Box my={4}>
              <Typography
                as="h1"
                color="black"
                fontSize={{ _: 24, tablet: "44px" }}
                fontWeight="300"
                lineHeight={{ _: "29px", tablet: "53px" }}
                mb={2}
              >
                Hello, how can we help?
              </Typography>
            </Box>
          )}
          <SearchBar>
            <Flex flexWrap="wrap" px={2}>
              <Box width={1}>
                <Search
                  onChange={(e) => setSearchValue(e.target.value)}
                  placeholder="Search"
                  value={searchValue}
                />
              </Box>
            </Flex>
          </SearchBar>
        </Box>
      </Header>
      <Box contain pb={{ _: 4, tablet: 6 }} px={{ _: 0, tablet: 0 }}>
        <Switch>
          <Route
            exact
            path={"/helpdesk/search"}
            render={({ match }) => (
              <Breadcrumbs match={match}>
                <SearchResults searchValue={searchValue} />
              </Breadcrumbs>
            )}
          />

          <Route
            exact
            path="/helpdesk/contact"
            render={({ match }) => (
              <Breadcrumbs match={match}>
                <Contact />
              </Breadcrumbs>
            )}
          />

          <Route
            exact
            path={"/helpdesk/:collection_id"}
            render={({ match }) => {
              return (
                <Collection
                  helpdeskCollection={helpdeskCollection}
                  match={match}
                  staticContext={staticContext}
                />
              )
            }}
          />
          <Route
            exact
            path={"/helpdesk/:collection_id/:category_id"}
            render={({ match }) => {
              return (
                <Category
                  helpdeskCollection={helpdeskCollection}
                  match={match}
                  staticContext={staticContext}
                />
              )
            }}
          />
          <Route
            exact
            path={"/helpdesk/a/:article_id"}
            render={({ match }) => (
              <WrappedArticle
                collections={collections}
                helpdeskArticle={helpdeskArticle}
                helpdeskCollection={helpdeskCollection}
                helpdeskRelatedArticles={helpdeskRelatedArticles}
                location={location}
                match={match}
                staticContext={staticContext}
              />
            )}
          />
          <Route
            exact
            path={"/helpdesk/a/:article_id/:seo"}
            render={({ match }) => (
              <WrappedArticle
                collections={collections}
                helpdeskArticle={helpdeskArticle}
                helpdeskCollection={helpdeskCollection}
                helpdeskRelatedArticles={helpdeskRelatedArticles}
                location={location}
                match={match}
                staticContext={staticContext}
              />
            )}
          />
          <Route
            exact
            path="/helpdesk"
            render={({ match }) => (
              <Homepage helpdeskCollection={helpdeskCollection} match={match} />
            )}
          />
        </Switch>
      </Box>
    </Box>
  )
}
Helpdesk.propTypes = {
  collections: PropTypes.object,
  fetchResults: PropTypes.func,
  helpdeskArticle: PropTypes.object,
  helpdeskCollection: PropTypes.object,
  helpdeskRelatedArticles: PropTypes.array,
  location: PropTypes.object,
  match: PropTypes.object,
  staticContext: PropTypes.object,
}

export default withHelpdesk(Helpdesk)
